<template>
    <section>
        <q-banner v-if="aiFieldCount" class="bg-blue-1 text-blue-10 q-mb-md" rounded>
            <q-icon name="auto_awesome" class="q-mr-xs" />
            The referral filled in {{ aiFieldCount }} field{{ aiFieldCount === 1 ? "" : "s" }}.
            Check each value and make any changes needed.
        </q-banner>

        <div class="row q-col-gutter-md">
            <div class="col-12 col-md-6">
                <p class="q-mb-xs"><strong>Service</strong></p>
                <q-select
                    :model-value="draft.serviceCode"
                    :options="serviceOptions"
                    outlined
                    dense
                    emit-value
                    map-options
                    :rules="[value => !!value || 'Service is required.']"
                    @update:model-value="value => updateField('serviceCode', value)"
                />
            </div>
            <div class="col-12 col-md-6">
                <p class="q-mb-xs"><strong>Primary Worker</strong></p>
                <q-select
                    :model-value="draft.assignedWorker"
                    :options="staffOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    @update:model-value="value => { removePrimaryFromAdditionalWorkers(value); updateField('assignedWorker', value); }"
                />
            </div>
            <div class="col-12 col-md-6">
                <p class="q-mb-xs"><strong>Additional Workers</strong></p>                
                <q-select
                    :model-value="draft.additionalWorkers"
                    :options="additionalWorkerOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    multiple
                    use-chips
                    @update:model-value="value => updateField('additionalWorkers', value)"
                />
            </div>
            <div class="col-12 col-md-6">
                <p class="q-mb-xs"><strong>Referral Source</strong></p>
                <q-select
                    :model-value="draft.referralSource"
                    :options="referralSourceOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    @update:model-value="value => updateField('referralSource', value)"
                />
            </div>
            <div class="col-12 col-md-6">
                <p class="q-mb-xs"><strong>Needs</strong></p>
                <q-select
                    :model-value="draft.riskFactor"
                    :options="riskOptions"
                    outlined
                    dense
                    clearable
                    emit-value
                    map-options
                    @update:model-value="value => updateField('riskFactor', value)"
                />
            </div>
            <div class="col-12 col-md-6">
                <p class="q-mb-xs"><strong>Entry Date</strong></p>
                <TkDateField
                    :model-value="draft.enteredDate"
                    :rules="[value => !!value || 'Entry Date is required.']"
                    @update:model-value="value => updateField('enteredDate', value)"
                />
            </div>
        </div>

        <div class="q-mt-lg">
            <p class="q-mb-xs"><strong>Required Checklist Items</strong></p>
            <q-option-group
                :model-value="draft.checklistDefinitionIds"
                :options="checklistOptions"
                type="checkbox"
                dense
                color="primary"
                @update:model-value="value => updateField('checklistDefinitionIds', value)"
            />
            <div class="row q-col-gutter-sm q-mt-sm items-center">
                <div class="col">
                    <q-input
                        :model-value="draft.customChecklistInput"
                        outlined
                        dense
                        clearable
                        label="Custom checklist item"
                        @keyup.enter.stop.prevent="addCustomChecklistItem"
                        @update:model-value="value => updateField('customChecklistInput', value)"
                    />
                </div>
                <div class="col-auto">
                    <q-btn
                        outline
                        no-caps
                        color="primary"
                        label="Add"
                        :disable="!String(draft.customChecklistInput || '').trim()"
                        @click="addCustomChecklistItem"
                    />
                </div>
            </div>
            <div v-if="draft.customChecklistLabels.length" class="custom-chip-row">
                <q-chip
                    v-for="label in draft.customChecklistLabels"
                    :key="label"
                    removable
                    dense
                    color="grey-2"
                    text-color="dark"
                    @remove="removeCustomChecklistItem(label)"
                >
                    {{ label }}
                </q-chip>
            </div>
        </div>
    </section>
</template>

<script>
import TkDateField from "@/components/TkClients/TkDateField.vue";

export default {
    name: "EngagementDraftFields",
    components: { TkDateField },
    emits: ["update:draft"],
    props: {
        draft: {
            type: Object,
            required: true,
        },
        serviceOptions: {
            type: Array,
            required: true,
        },
        staffOptions: {
            type: Array,
            required: true,
        },
        referralSourceOptions: {
            type: Array,
            required: true,
        },
        riskOptions: {
            type: Array,
            required: true,
        },
        checklistOptions: {
            type: Array,
            required: true,
        },
        extractionFields: {
            type: Array,
            required: true,
        },
        additionalWorkerOptions: {
            type: Array,
            required: true,
        },
    },
    computed: {
        aiFields() {
            return this.extractionFields.filter(field => (
                field.destination?.entity === "engagement"
                && field.reviewedValue !== null
                && field.reviewedValue !== ""
            ));
        },
        aiFieldCount() {
            return this.aiFields.length;
        },
    },
    methods: {
        updateField(field, value) {
            this.$emit("update:draft", {
                ...this.draft,
                [field]: value,
            });
        },
        removePrimaryFromAdditionalWorkers(workerId) {
            this.updateField(
                "additionalWorkers",
                (this.draft.additionalWorkers || []).filter(id => id !== workerId)
            );
        },
        addCustomChecklistItem() {
            const label = String(this.draft.customChecklistInput || "").trim();
            if (!label || this.draft.customChecklistLabels.includes(label)) return;
            this.$emit("update:draft", {
                ...this.draft,
                customChecklistLabels: [...this.draft.customChecklistLabels, label],
                customChecklistInput: "",
            });
        },
        removeCustomChecklistItem(label) {
            this.updateField(
                "customChecklistLabels",
                this.draft.customChecklistLabels.filter(item => item !== label)
            );
        },
    },
};
</script>

<style scoped>
.custom-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}
</style>
